import React, { useState, useEffect, useRef, useMemo } from 'react';
import { format, isSameDay } from 'date-fns';
import { es } from 'date-fns/locale';
import { Send, CheckCircle, Trash2 } from 'lucide-react';
import Card, { CardHeader, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import { useAppStore } from '../store';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { ChatMessage } from '../types';
import DeleteConfirmModal from '../components/modals/DeleteConfirmModal';

const ChatEnvios: React.FC = () => {
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [cooldown, setCooldown] = useState(false);
  const [cooldownTime, setCooldownTime] = useState(0);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState<string | null>(null);
  const [localMessages, setLocalMessages] = useState<ChatMessage[]>([]);
  const [userEmails, setUserEmails] = useState<Record<string, string>>({});
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const subscriptionRef = useRef<any>(null);
  const { chatMessages, fetchChatMessages, addChatMessage, toggleChatMessageReaction, deleteChatMessage } = useAppStore();
  const { user, userRole, isAdmin } = useAuth();
  
  // Fetch messages on component mount and set up real-time subscription
  useEffect(() => {
    const loadMessages = async () => {
      try {
        // Fetch messages directly from the database
        const { data, error } = await supabase
          .from('chat_messages')
          .select('*')
          .eq('channel', 'envios')
          .order('created_at', { ascending: true });
        
        if (error) throw error;
        
        // Update local state with fetched messages
        setLocalMessages(data || []);
        
        // Collect all unique user IDs
        const userIds = [...new Set((data || []).map(msg => msg.user_id))];
        fetchUserEmails(userIds);
      } catch (error) {
        console.error('Error loading chat messages:', error);
      }
    };
    
    // Initial load
    loadMessages();
    
    // Set up real-time subscription
    const channel = supabase
      .channel('chat_messages_envios')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chat_messages',
          filter: 'channel=eq.envios',
        },
        (payload) => {
          // Handle different types of changes
          if (payload.eventType === 'INSERT') {
            const newMessage = payload.new as ChatMessage;
            
            // Check if message already exists in local state to prevent duplicates
            if (!localMessages.some(msg => msg.id === newMessage.id)) {
              setLocalMessages(prev => [...prev, newMessage]);
              
              // Fetch user email if not already in cache
              if (newMessage.user_id && !userEmails[newMessage.user_id]) {
                fetchUserEmails([newMessage.user_id]);
              }
            }
          } else if (payload.eventType === 'UPDATE') {
            const updatedMessage = payload.new as ChatMessage;
            
            // Update the specific message
            setLocalMessages(prev => 
              prev.map(msg => msg.id === updatedMessage.id ? updatedMessage : msg)
            );
          } else if (payload.eventType === 'DELETE') {
            const deletedMessage = payload.old as ChatMessage;
            
            // Remove the deleted message
            setLocalMessages(prev => 
              prev.filter(msg => msg.id !== deletedMessage.id)
            );
          }
        }
      )
      .subscribe();
    
    // Store subscription reference for cleanup
    subscriptionRef.current = channel;
    
    // Cleanup subscription on unmount
    return () => {
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
      }
    };
  }, []);
  
  // Fetch user emails for all message authors
  const fetchUserEmails = async (userIds: string[]) => {
    try {
      // Use the safe_users view instead of auth.users
      const { data, error } = await supabase
        .from('safe_users')
        .select('id, email')
        .in('id', userIds);
      
      if (error) {
        // If we can't access safe_users directly, try using the edge function
        const { data: usersData, error: funcError } = await supabase.functions.invoke('list-users');
        
        if (funcError) throw funcError;
        
        // Create a map of user IDs to emails
        const emailMap: Record<string, string> = {};
        usersData.users.forEach((user: any) => {
          emailMap[user.id] = user.email;
        });
        
        setUserEmails(prev => ({...prev, ...emailMap}));
      } else if (data) {
        // Create a map of user IDs to emails
        const emailMap: Record<string, string> = {};
        data.forEach(user => {
          emailMap[user.id] = user.email;
        });
        
        setUserEmails(prev => ({...prev, ...emailMap}));
      }
    } catch (error) {
      console.error('Error fetching user emails:', error);
    }
  };
  
  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [localMessages]);
  
  // Handle cooldown timer
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (cooldown && cooldownTime > 0) {
      interval = setInterval(() => {
        setCooldownTime(prev => {
          if (prev <= 1) {
            setCooldown(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [cooldown, cooldownTime]);
  
  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [newMessage]);
  
  const handleSendMessage = async () => {
    if (!user || !newMessage.trim() || isLoading) return;
        /* ============  AÑADIR (mensaje instantáneo) ============ */
    // 1) Creamos un “mensaje temporal” con un ID inventado
    const tempId = crypto.randomUUID();           // id falso
    const now    = new Date().toISOString();      // fecha/hora actual

    const tempMessage = {
      id: tempId,
      channel: 'envios',
      user_id: user.id,
      message: newMessage.trim(),
      reactions: [],
      created_at: now,
    };

    // 2) Lo pintamos al instante en la pantalla
    setLocalMessages(prev => [...prev, tempMessage]);
    /* ======================================================= */

    // Check message length
    if (newMessage.length > 1000) {
      alert('El mensaje no puede superar los 1000 caracteres');
      return;
    }
    
    try {
      setIsLoading(true);
      
      // Create new message
      const messageData = {
        channel: 'envios',
        user_id: user.id,
        message: newMessage.trim(),
        reactions: []
      };
      
      // Add message to database
const { data, error } = await supabase
  .from('chat_messages')
  .insert([messageData])
  .select()          // ← hace que Supabase devuelva la fila real
  .single();

if (error) throw error;

/* ============  AÑADIR (reemplazar temporal) ============ */
// cambiamos el mensaje temporal (id falso) por el real (id de la base)
setLocalMessages(prev =>
  prev.map(m => (m.id === tempId ? data : m))
);
/* ======================================================= */

      
      if (error) throw error;
      
      // Clear input
      setNewMessage('');
      
      // Set cooldown for non-admin users
      if (userRole !== 'Administrador') {
        setCooldown(true);
        setCooldownTime(10);
      }
      
      // Scroll to bottom
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  
  const handleReaction = async (messageId: string) => {
    if (!user) return;
    
    try {
      // Find the message in local state
      const message = localMessages.find(m => m.id === messageId);
      if (!message) return;
      
      // Check if user already reacted
      const hasReacted = message.reactions.includes(user.id);
      
    const newReactions = hasReacted
      ? message.reactions.filter(id => id !== user.id)
      : [...message.reactions, user.id];

    /* 🔶 ACTUALIZACIÓN INSTANTÁNEA EN PANTALLA 🔶 */
    setLocalMessages(prev =>
      prev.map(m =>
        m.id === messageId ? { ...m, reactions: newReactions } : m
      )
    );
    /* ------------------------------------------- */

    // Ahora sí, avisamos a la base (llegará al resto por la subscripción)
    const { error } = await supabase
      .from('chat_messages')
      .update({ reactions: newReactions })
      .eq('id', messageId);

      
      if (error) throw error;
    } catch (error) {
      console.error('Error toggling reaction:', error);
    }
  };
  
  const handleDeleteMessage = (messageId: string) => {
    setMessageToDelete(messageId);
    setDeleteModalOpen(true);
  };
  
  const confirmDeleteMessage = async () => {
    if (!messageToDelete) return;
    
    try {
      // Delete from database
      const { error } = await supabase
        .from('chat_messages')
        .delete()
        .eq('id', messageToDelete);
      
      if (error) throw error;
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };
  
  // Function to format message text with clickable links
  const formatMessageWithLinks = (text: string) => {
    // Regular expression to match URLs
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    
    // If no URLs in the text, return the text as is
    if (!urlRegex.test(text)) {
      return text;
    }
    
    // Create an array to hold the result
    const result = [];
    let lastIndex = 0;
    let match;
    let index = 0;
    
    // Find all URLs and replace them with links
    const regex = new RegExp(urlRegex);
    while ((match = regex.exec(text)) !== null) {
      // Add the text before the URL
      if (match.index > lastIndex) {
        result.push(text.substring(lastIndex, match.index));
      }
      
      const url = match[0];
      // Truncate URL for display if it's too long
      const displayUrl = truncateUrl(url);
      
      // Add the URL as a link
      result.push(
        <a 
          key={index++} 
          href={url} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="text-blue-500 hover:underline"
          title={url}
        >
          {displayUrl}
        </a>
      );
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add any remaining text
    if (lastIndex < text.length) {
      result.push(text.substring(lastIndex));
    }
    
    return result;
  };
  
  // Function to truncate long URLs
  const truncateUrl = (url: string) => {
    const maxLength = 40; // Maximum length before truncation
    
    if (url.length <= maxLength) {
      return url;
    }
    
    // Try to get domain name
    try {
      const urlObj = new URL(url);
      const domain = urlObj.hostname;
      
      // If the URL has a path, truncate it
      if (urlObj.pathname && urlObj.pathname !== '/') {
        return `${domain}${urlObj.pathname.substring(0, 15)}...`;
      }
      
      // If it's just a domain, return it
      return domain;
    } catch (e) {
      // If URL parsing fails, just truncate the string
      return url.substring(0, maxLength - 3) + '...';
    }
  };
  
  // Get user email from user ID
  const getUserEmail = (userId: string) => {
    // If we have the email in our cache, return it
    if (userEmails[userId]) {
      return userEmails[userId];
    }
    
    // If it's the current user, return their email
    if (user && userId === user.id) {
      return user.email || 'Usuario';
    }
    
    // Otherwise return a placeholder
    return 'Usuario';
  };
  
  // Check if user can delete a message
  const canDeleteMessage = (message: ChatMessage) => {
    if (!user) return false;
    
    // Admins can delete any message
    if (isAdmin) return true;
    
    // Users can delete their own messages
    return message.user_id === user.id;
  };

  // Function to format date for the date separator
  const formatDateForSeparator = (date: Date) => {
    return format(date, "EEEE, d 'de' MMMM 'de' yyyy", { locale: es });
  };
  
  return (
    <div className="h-[calc(100vh-12rem)] flex flex-col">
      <Card className="flex-1 flex flex-col">
        <CardHeader title="Chat de Envíos" />
        
        <CardContent className="flex-1 overflow-y-auto p-0" ref={chatContainerRef}>
          {localMessages.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-500 dark:text-gray-400">No hay mensajes aún. ¡Sé el primero en escribir!</p>
            </div>
          ) : (
            <div className="p-4 space-y-4">
              {localMessages.map((message, index) => {
                // Check if we need to add a date separator
                const showDateSeparator = index === 0 || 
                  !isSameDay(new Date(message.created_at), new Date(localMessages[index - 1].created_at));
                
                return (
                  <React.Fragment key={message.id}>
                    {showDateSeparator && (
                      <div className="flex items-center my-4">
                        <div className="flex-grow border-t border-gray-300 dark:border-gray-700"></div>
                        <span className="mx-4 text-xs text-gray-500 dark:text-gray-400 font-medium">
                          {formatDateForSeparator(new Date(message.created_at))}
                        </span>
                        <div className="flex-grow border-t border-gray-300 dark:border-gray-700"></div>
                      </div>
                    )}
                    
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-3 hover:shadow">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <span className="font-medium text-sm text-gray-900 dark:text-white">
                              {getUserEmail(message.user_id)}
                            </span>
                            <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                              {format(new Date(message.created_at), 'HH:mm')}
                            </span>
                            
                            {canDeleteMessage(message) && (
                              <button 
                                onClick={() => handleDeleteMessage(message.id)}
                                className="ml-2 text-gray-400 hover:text-red-500 dark:hover:text-red-400 focus:outline-none"
                                title="Eliminar mensaje"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                          
                          <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap mt-1">
                            {formatMessageWithLinks(message.message)}
                          </p>
                        </div>
                      </div>
                      
                      <div className="mt-2 flex items-center">
                        <button 
                          onClick={() => handleReaction(message.id)}
                          className={`flex items-center px-2 py-1 rounded-full ${
                            message.reactions.includes(user?.id || '') 
                              ? 'bg-green-500 text-white' 
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 hover:bg-green-50 dark:hover:bg-green-900/10'
                          }`}
                          title="Marcar como visto"
                        >
                          <CheckCircle className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          )}
        </CardContent>
        
        <div className="p-3 border-t border-gray-200 dark:border-gray-700">
          <div className="flex flex-col space-y-1">
            <textarea
              ref={textareaRef}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Escribe un mensaje..."
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white resize-none min-h-[60px] max-h-[150px] text-sm"
              disabled={cooldown || isLoading}
              maxLength={1000}
            />
            
            <div className="flex justify-between items-center">
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {cooldown && (
                  <span>Podrás enviar otro mensaje en {cooldownTime} segundos</span>
                )}
                {newMessage.length > 0 && (
                  <span>{newMessage.length}/1000 caracteres</span>
                )}
              </div>
              
              <Button
                onClick={handleSendMessage}
                disabled={!newMessage.trim() || cooldown || isLoading}
                icon={<Send className="h-4 w-4" />}
                size="sm"
              >
                Enviar
              </Button>
            </div>
          </div>
        </div>
      </Card>
      
      <DeleteConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setMessageToDelete(null);
        }}
        onConfirm={confirmDeleteMessage}
        title="Eliminar mensaje"
        message="¿Estás seguro de que deseas eliminar este mensaje? Esta acción no se puede deshacer."
      />
    </div>
  );
};

export default ChatEnvios;